import { useCallback, useMemo, useRef } from "react";
import Input, { InputType } from "components/shared/inputs/input";
import { SubAppCode } from "utils/engine/consts";
import { UseFormGetFieldState } from "react-hook-form";
import { EtudeFormFields } from "../constants";

export function useGetSubAppFields({
  subApplication,
  control,
  allData,
  t,
  getFieldState,
  watch,
  NbMPJ,
  totalSum,
  autoRecalc,
  recalc,
  getValues,
  setLoadingCalculatedData
}: {
  subApplication: (typeof SubAppCode)[keyof typeof SubAppCode] | null;
  control: any;
  allData: any;
  t: (key: string) => string;
  getFieldState: UseFormGetFieldState<EtudeFormFields>;
  watch: (name: string) => any;
  NbMPJ: any;
  totalSum: number;
  autoRecalc: boolean;
  recalc: () => void;
  getValues: (name?: keyof EtudeFormFields) => any;
  setLoadingCalculatedData: (loading: boolean) => void;
}) {
  const prevValues = useRef<Partial<EtudeFormFields> | null>(getValues());

  const handleRecalcEvent = useCallback(
    (fieldName: keyof EtudeFormFields) =>
      async (e: React.KeyboardEvent | React.FocusEvent) => {
        if (!autoRecalc || !getFieldState) return;

        const isEnter = (e as React.KeyboardEvent).key === "Enter";
        const isBlur = e.type === "blur";

        if (!isEnter && !isBlur) return;
        const current = getValues(fieldName);
        const prev = prevValues.current?.[fieldName];

        if (current === prev) return;
        setLoadingCalculatedData(true)

        e.preventDefault?.();

        setTimeout(async () => {
          await recalc();
          prevValues.current = getValues();
        }, 2000); // delay to allow Expert mode values to update
      },
    [autoRecalc, getFieldState, getValues, recalc]
  );

  const handleSelectChange = useCallback(
    (_val: any, _option?: any) => {
      if (autoRecalc) {
        setLoadingCalculatedData(true)
        setTimeout(() => {
          recalc();
        }, 2000);
      }
    },
    [autoRecalc, recalc]
  );

  const result = useMemo(() => {
    switch (subApplication) {
      case "prives":
      case "sociaux":
        return (
          <group data-gap="10">
            <group data-direction="column" data-gap="10" data-length="autofit">
              <Input
                type={InputType.Number}
                control={control}
                name="nbT1"
                label="T1"
                minValue={allData?.nbT1?.minimum}
                maxValue={allData?.nbT1?.maximum}
                onBlur={handleRecalcEvent("nbT1")}
                onKeyDown={handleRecalcEvent("nbT1")}
              />
              <Input
                type={InputType.Number}
                control={control}
                name="nbT2"
                label="T2"
                minValue={allData?.nbT2?.minimum}
                maxValue={allData?.nbT2?.maximum}
                onBlur={handleRecalcEvent("nbT2")}
                onKeyDown={handleRecalcEvent("nbT2")}
              />
              <Input
                type={InputType.Number}
                control={control}
                name="nbT3"
                label="T3"
                minValue={allData?.nbT3?.minimum}
                maxValue={allData?.nbT3?.maximum}
                onBlur={handleRecalcEvent("nbT3")}
                onKeyDown={handleRecalcEvent("nbT3")}
              />
            </group>
            <group data-direction="column" data-gap="10" data-length="autofit">
              <Input
                type={InputType.Number}
                control={control}
                name="nbT4"
                label="T4"
                minValue={allData?.nbT4?.minimum}
                maxValue={allData?.nbT4?.maximum}
                onBlur={handleRecalcEvent("nbT4")}
                onKeyDown={handleRecalcEvent("nbT4")}
              />
              <Input
                type={InputType.Number}
                control={control}
                name="nbT5"
                label="T5"
                minValue={allData?.nbT5?.minimum}
                maxValue={allData?.nbT5?.maximum}
                onBlur={handleRecalcEvent("nbT5")}
                onKeyDown={handleRecalcEvent("nbT5")}
              />
              <Input
                type={InputType.Number}
                control={control}
                name="nbT6"
                label="T6"
                minValue={allData?.nbT6?.minimum}
                maxValue={allData?.nbT6?.maximum}
                onBlur={handleRecalcEvent("nbT6")}
                onKeyDown={handleRecalcEvent("nbT6")}
              />
            </group>
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              dataLength="400"
              onChangeCustom={handleSelectChange}
            />

            {/* Total sum row */}
            {totalSum > 0 && (
              <group data-direction="row" data-gap="10" data-align="center">
                <span style={{ fontWeight: "bold", minWidth: "100px" }}>
                  {t("FO_NB_LOCATION") || "FO_NB_LOCATION"}: {totalSum}
                </span>
              </group>
            )}
          </group>
        );
      case "seniors":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_logements"
              name="nbT1"
              helpText={t("FO_HELP_SENIOR_NBL")}
              minValue={allData?.nbT1?.minimum}
              maxValue={allData?.nbT1?.maximum}
              onBlur={handleRecalcEvent("nbT1")}
              onKeyDown={handleRecalcEvent("nbT1")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "etudiant":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              label="nombre_de_lits"
              name="NbLit"
              helpText={t("FO_HELP_STUDENT_NBL")}
              type={InputType.Number}
              minValue={allData?.NbLit?.minimum}
              maxValue={allData?.NbLit?.maximum}
              onBlur={handleRecalcEvent("NbLit")}
              onKeyDown={handleRecalcEvent("NbLit")}
            />
            <Input
              control={control}
              label="nombre_de_couverts_midi"
              type="number"
              name="NbCouvM"
              minValue={allData?.NbCouvM?.minimum}
              maxValue={allData?.NbCouvM?.maximum}
              onBlur={handleRecalcEvent("NbCouvM")}
              onKeyDown={handleRecalcEvent("NbCouvM")}
            />
            <Input
              control={control}
              label="nombre_de_couverts_soir"
              type="number"
              name="NbCouvS"
              minValue={allData?.NbCouvS?.minimum}
              maxValue={allData?.NbCouvS?.maximum}
              onBlur={handleRecalcEvent("NbCouvS")}
              onKeyDown={handleRecalcEvent("NbCouvS")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "travailleur":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              label="nombre_de_lits"
              name="NbLit"
              helpText={t("FO_HELP_YOUNGWORKER_NBL")}
              type={InputType.Number}
              minValue={allData?.NbLit?.minimum}
              maxValue={allData?.NbLit?.maximum}
              onBlur={handleRecalcEvent("NbLit")}
              onKeyDown={handleRecalcEvent("NbLit")}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_couverts_midi"
              name="NbCouvM"
              minValue={allData?.NbCouvM?.minimum}
              maxValue={allData?.NbCouvM?.maximum}
              onBlur={handleRecalcEvent("NbCouvM")}
              onKeyDown={handleRecalcEvent("NbCouvM")}
            />
            <Input
              control={control}
              label="nombre_de_couverts_soir"
              type="number"
              name="NbCouvS"
              minValue={allData?.NbCouvS?.minimum}
              maxValue={allData?.NbCouvS?.maximum}
              onBlur={handleRecalcEvent("NbCouvS")}
              onKeyDown={handleRecalcEvent("NbCouvS")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "tourisme":
      case "affaires":
      case "montagne":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Select}
              options={allData?.NbE?.enum}
              label="nombre_detoiles_de_lhotel"
              name="NbE"
              minValue={allData?.NbE?.minimum}
              maxValue={allData?.NbE?.maximum}
              onChangeCustom={() => {
                if (autoRecalc) recalc();
              }}
            />
            <Input
              type={InputType.Number}
              control={control}
              label="nombre_de_chambres"
              name="NbC"
              minValue={allData?.NbC?.minimum}
              maxValue={allData?.NbC?.maximum}
              onBlur={handleRecalcEvent("NbC")}
              onKeyDown={handleRecalcEvent("NbC")}
            />
            <Input
              type={InputType.Number}
              control={control}
              label="nombre_de_couverts_midi"
              name="NbCouvM"
              minValue={allData?.NbCouvM?.minimum}
              maxValue={allData?.NbCouvM?.maximum}
              onBlur={handleRecalcEvent("NbCouvM")}
              onKeyDown={handleRecalcEvent("NbCouvM")}
            />
            <Input
              type={InputType.Number}
              control={control}
              label="nombre_de_couverts_soir"
              name="NbCouvS"
              minValue={allData?.NbCouvS?.minimum}
              maxValue={allData?.NbCouvS?.maximum}
              onBlur={handleRecalcEvent("NbCouvS")}
              onKeyDown={handleRecalcEvent("NbCouvS")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "camping":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Select}
              options={allData?.NbE?.enum}
              label="nombre_detoiles_du_camping"
              name="NbE"
              minValue={allData?.NbE?.minimum}
              maxValue={allData?.NbE?.maximum}
              onChangeCustom={handleSelectChange}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_douches_du_bloc_sanitaire"
              name="NbD"
              helpText={t("FO_HELP_CAMPING_NBD")}
              minValue={allData?.NbD?.minimum}
              maxValue={allData?.NbD?.maximum}
              onBlur={handleRecalcEvent("NbD")}
              onKeyDown={handleRecalcEvent("NbD")}
            />
            <Input
              control={control}
              label="nombre_demplacements_desservis_par_le_bloc_sanitaire"
              type="number"
              name="NbEm"
              helpText="FO_HELP_CAMPING_NB_EMP"
              minValue={allData?.NbEm?.minimum}
              maxValue={allData?.NbEm?.maximum}
              onBlur={handleRecalcEvent("NbEm")}
              onKeyDown={handleRecalcEvent("NbEm")}
            />
            <Input
              control={control}
              label="bord_de_mer"
              type={InputType.Select}
              options={[
                { text: "FO_ETU_NO", value: 1 },
                { text: "FO_ETU_YES", value: 2 },
              ]}
              name="bdm"
              onChangeCustom={handleSelectChange}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "football":
      case "rugby":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              label="nombre_de_postes_de_douches"
              type={InputType.Number}
              name="NbD"
              minValue={allData?.NbD?.minimum}
              maxValue={allData?.NbD?.maximum}
              onBlur={handleRecalcEvent("NbD")}
              onKeyDown={handleRecalcEvent("NbD")}
            />
            <Input
              control={control}
              label="nombre_de_personnes_par_match"
              type={InputType.Number}
              name="PersActiv"
              helpText={t("FO_HELP_STADE_NBPERS")}
              minValue={allData?.PersActiv?.minimum}
              maxValue={allData?.PersActiv?.maximum}
              onBlur={handleRecalcEvent("PersActiv")}
              onKeyDown={handleRecalcEvent("PersActiv")}
            />
            <Input
              control={control}
              label="nombre_de_matchs_journalier"
              type={InputType.Select}
              options={allData?.NbMPJ?.enum}
              name="NbMPJ"
              helpText={t("FO_HELP_STADE_NBMATCH")}
              onChangeCustom={handleSelectChange}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="demarrage_d_un_match_toutes_les"
              name="TpsActSport"
              helpText="FO_HELP_STADE_DEMARRAGE"
              unit="min"
              disabled={NbMPJ === 1}
              minValue={allData?.TpsActSport?.minimum}
              maxValue={allData?.TpsActSport?.maximum}
              onBlur={handleRecalcEvent("TpsActSport")}
              onKeyDown={handleRecalcEvent("TpsActSport")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "gymnase":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_postes_de_douches"
              name="NbD"
              minValue={allData?.NbD?.minimum}
              maxValue={allData?.NbD?.maximum}
              onBlur={handleRecalcEvent("NbD")}
              onKeyDown={handleRecalcEvent("NbD")}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_personnes_en_simultane"
              name="personnes"
              minValue={allData?.personnes?.minimum}
              maxValue={allData?.personnes?.maximum}
              onBlur={handleRecalcEvent("personnes")}
              onKeyDown={handleRecalcEvent("personnes")}
            />
            <Input
              control={control}
              label="nombre_d_activites_sportives_journalieres"
              type={InputType.Select}
              options={allData?.NbActiv?.enum}
              name="NbActiv"
              onChangeCustom={() => {
                if (autoRecalc) recalc();
              }}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="demarrage_d_une_activite_sportive_toutes_les"
              name="TpsActSport"
              disabled={watch("NbActiv") === 1}
              minValue={allData?.TpsActSport?.minimum}
              maxValue={allData?.TpsActSport?.maximum}
              unit="min"
              onBlur={handleRecalcEvent("TpsActSport")}
              onKeyDown={handleRecalcEvent("TpsActSport")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "piscine":
      case "multisport":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Number}
              label="frequentation_journaliere"
              name="freq"
              minValue={allData?.freq?.minimum}
              maxValue={allData?.freq?.maximum}
              onBlur={handleRecalcEvent("freq")}
              onKeyDown={handleRecalcEvent("freq")}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_postes_de_douches"
              name="NbD"
              minValue={allData?.NbD?.minimum}
              maxValue={allData?.NbD?.maximum}
              onBlur={handleRecalcEvent("NbD")}
              onKeyDown={handleRecalcEvent("NbD")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "internat":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_postes_de_douches"
              name="NbD"
              minValue={allData?.NbD?.minimum}
              maxValue={allData?.NbD?.maximum}
              onBlur={handleRecalcEvent("NbD")}
              onKeyDown={handleRecalcEvent("NbD")}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_lits"
              name="NbLit"
              minValue={allData?.NbLit?.minimum}
              maxValue={allData?.NbLit?.maximum}
              onBlur={handleRecalcEvent("NbLit")}
              onKeyDown={handleRecalcEvent("NbLit")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "caserne":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_postes_de_douches"
              name="NbD"
              minValue={allData?.NbD?.minimum}
              maxValue={allData?.NbD?.maximum}
              onBlur={handleRecalcEvent("NbD")}
              onKeyDown={handleRecalcEvent("NbD")}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_personnes_par_session_de_douches"
              name="personnes"
              helpText={t("FO_HELP_CASERN_NBPERS")}
              minValue={allData?.personnes?.minimum}
              maxValue={allData?.personnes?.maximum}
              onBlur={handleRecalcEvent("personnes")}
              onKeyDown={handleRecalcEvent("personnes")}
            />
            <Input
              control={control}
              label="nombre_de_sessions_de_douches_par_jour"
              type={InputType.Select}
              options={allData?.NbPuisages?.enum}
              name="NbPuisages"
              onChangeCustom={handleSelectChange}
            />
            <Input
              control={control}
              label="une_session_de_douches_toutes_les"
              type={InputType.Number}
              name="TpsActSport"
              disabled={watch("NbPuisages") == 1}
              minValue={allData?.TpsActSport?.minimum}
              maxValue={allData?.TpsActSport?.maximum}
              onBlur={handleRecalcEvent("TpsActSport")}
              onKeyDown={handleRecalcEvent("TpsActSport")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "hopital":
      case "ehpad":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_lits"
              name="NbLE"
              minValue={allData?.NbLE?.minimum}
              maxValue={allData?.NbLE?.maximum}
              onBlur={handleRecalcEvent("NbLE")}
              onKeyDown={handleRecalcEvent("NbLE")}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_repas"
              name="NbRE"
              helpText={t("FO_HELP_HOSPITAL_EHPAD_NBREPAS")}
              minValue={allData?.NbRE?.minimum}
              maxValue={allData?.NbRE?.maximum}
              onBlur={handleRecalcEvent("NbRE")}
              onKeyDown={handleRecalcEvent("NbRE")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "traditionnel":
      case "gastronomique":
      case "rapide":
      case "collectif":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_repas_le_midi"
              name="NbRepasMidi"
              minValue={allData?.NbRepasMidi?.minimum}
              maxValue={allData?.NbRepasMidi?.maximum}
              onBlur={handleRecalcEvent("NbRepasMidi")}
              onKeyDown={handleRecalcEvent("NbRepasMidi")}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_repas_le_soir"
              name="NbRepasSoir"
              minValue={allData?.NbRepasSoir?.minimum}
              maxValue={allData?.NbRepasSoir?.maximum}
              onBlur={handleRecalcEvent("NbRepasSoir")}
              onKeyDown={handleRecalcEvent("NbRepasSoir")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "vestiaire":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_postes_de_douches"
              name="NbD"
              minValue={allData?.NbD?.minimum}
              maxValue={allData?.NbD?.maximum}
              onBlur={handleRecalcEvent("NbD")}
              onKeyDown={handleRecalcEvent("NbD")}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_personnes_par_session_de_douches"
              name="personnes"
              helpText={t("FO_HELP_OTHER_NBPERS")}
              minValue={allData?.personnes?.minimum}
              maxValue={allData?.personnes?.maximum}
              onBlur={handleRecalcEvent("personnes")}
              onKeyDown={handleRecalcEvent("personnes")}
            />
            <Input
              control={control}
              label="nombre_de_sessions_de_douches_par_jour"
              type={InputType.Select}
              options={allData?.NbPuisages?.enum}
              name="NbPuisages"
              onChangeCustom={() => {
                if (autoRecalc) recalc();
              }}
            />
            <Input
              control={control}
              type={InputType.Number}
              label="une_session_de_douches_toutes_les"
              name="TpsActSport"
              disabled={watch("NbPuisages") === 1}
              minValue={allData?.TpsActSport?.minimum}
              maxValue={allData?.TpsActSport?.maximum}
              onBlur={handleRecalcEvent("TpsActSport")}
              onKeyDown={handleRecalcEvent("TpsActSport")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "station":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              type={InputType.Number}
              label="nombre_de_pistes"
              name="Piste"
              minValue={allData?.Piste?.minimum}
              maxValue={allData?.Piste?.maximum}
              onBlur={handleRecalcEvent("Piste")}
              onKeyDown={handleRecalcEvent("Piste")}
            />
            <Input
              control={control}
              label="debit_nominal_lavage_brosse"
              type="number"
              name="QBrosse"
              minValue={allData?.QBrosse?.minimum}
              maxValue={allData?.QBrosse?.maximum}
              onBlur={handleRecalcEvent("QBrosse")}
              onKeyDown={handleRecalcEvent("QBrosse")}
            />
            <Input
              control={control}
              label="debit_nominal_lavage_haute_pression"
              type="number"
              name="QHp"
              minValue={allData?.QHp?.minimum}
              maxValue={allData?.QHp?.maximum}
              onBlur={handleRecalcEvent("QHp")}
              onKeyDown={handleRecalcEvent("QHp")}
            />
            <Input
              control={control}
              label="temperature_de_lavage"
              type="select"
              options={allData?.TEC?.enum}
              name="TEC"
              onChangeCustom={handleSelectChange}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      case "indus":
        return (
          <group data-gap="10" data-direction="column">
            <Input
              control={control}
              label="tempErature_de_puisage"
              type="select"
              options={allData?.TEC?.enum}
              name="TEC"
              helpText={t("FO_HELP_INDUSTRY_TEMP_PUIS")}
              onChangeCustom={handleSelectChange}
            />
            <Input
              control={control}
              label="volume_deau_chaude_par_cycle"
              type="number"
              name="BEC"
              minValue={allData?.BEC?.minimum}
              maxValue={allData?.BEC?.maximum}
              onBlur={handleRecalcEvent("BEC")}
              onKeyDown={handleRecalcEvent("BEC")}
            />
            <Input
              control={control}
              label="duree_dun_cycle"
              type="number"
              name="Pointe"
              helpText="FO_HELP_INDUSTRY_CYCLE"
              minValue={allData?.Pointe?.minimum}
              maxValue={allData?.Pointe?.maximum}
              onBlur={handleRecalcEvent("Pointe")}
              onKeyDown={handleRecalcEvent("Pointe")}
            />
            <Input
              control={control}
              label="nombre_de_cycles_de_puisage_par_jour"
              type="select"
              options={allData?.NbPuisages?.enum}
              name="NbPuisages"
              onChangeCustom={handleSelectChange}
            />
            <Input
              control={control}
              label="temps_de_pause_entre_2_cycles"
              type="number"
              name="TpsRetCons"
              disabled={watch("NbPuisages") === 1}
              minValue={allData?.TpsRetCons?.minimum}
              maxValue={allData?.TpsRetCons?.maximum}
              onBlur={handleRecalcEvent("TpsRetCons")}
              onKeyDown={handleRecalcEvent("TpsRetCons")}
            />
            <Input
              control={control}
              name="TEFS"
              type={InputType.Select}
              options={allData?.TEFS?.enum}
              label="temperature_minimale_deau_froide"
              onChangeCustom={handleSelectChange}
            />
          </group>
        );
      default:
        return <></>;
    }
  }, [
    subApplication,
    control,
    allData,
    t,
    watch,
    NbMPJ,
    totalSum,
    handleRecalcEvent,
    handleSelectChange,
  ]);

  return result;
}
